/* istanbul ignore file */

import type { RefObject } from 'react';
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { StyleSheet, TouchableOpacity, View } from 'react-native';

import { useSelector } from '@xstate5/react';

import {
  Button,
  IconLink,
  LoadingPlaceholder,
  theme,
  usePressableState,
  useResponsive,
  useIsFocused,
  useToggleState,
  webOnlyStyles,
} from '@sg/garnish';

import { useFeatureFlag } from '@order/LaunchDarkly';

import { getLocationDistanceInMiles } from '../../../../../../helpers/location-distance';
import { useTooFarLocationThresholdInMiles } from '../../../../../../hooks/useTooFarLocationThresholdInMiles';
import { getLocationOrderChannel } from '../../../../helpers';
import { usePredictions } from '../../../../hooks';
import { type LocationSearchService } from '../../../../machine';
import { type Location, type PositionCoordinates } from '../../../../machine';
import { useLocationResultsFeeDisclosure } from '../../hooks';
import {
  LOCATION_RESULTS_CARD_HEIGHT_PADDING_VERTICAL_SM,
  LOCATION_RESULTS_CARD_HEIGHT_PADDING_VERTICAL_XS,
} from '../../LocationResults.constants';
import { LocationResultCardDetailsModal, LocationResultCardDistanceModal } from './subcomponents';
import { LocationResultCardContentV2 } from './variations';

import type { OnSelectedLocationCardPress } from './LocationResultCard.types';

// ─────────────────────────────────────────────────────────────────────────────

export const LocationResultsCard = memo((props: LocationResultsCardProps) => {
  const {
    locationSearchActorRef,
    location,
    withDetailsModal,
    withoutOrderNowBtn,
    shouldShowLocationOrderChannel = false,
    isLoading = false,
    isDisabled = false,
    onPress,
    onLayout,
    onFocus,
    onUmount,
  } = props;

  const {
    id,
    name,
    address,
    city,
    state,
    zipCode,
    estimatedDeliveryTime,
    restaurantSlug,
    isClosed,
    acceptingOrders = true,
    lat: latitude,
    lng: longitude,
    isDelivery,
  } = location;

  const { formatMessage } = useIntl();

  const { isSurfaceDistanceForPredictionEnabled } = usePredictions({ locationSearchActorRef });

  const locationOrderChannel = getLocationOrderChannel(location);
  const isDeliveryLocation = locationOrderChannel === 'delivery';

  // ─── State ───────────────────────────────────────────────────────────

  const { value: isDistanceModalVisible, toggle: toggleDistanceModal } = useToggleState();

  // ─── Derived Data ────────────────────────────────────────────────────

  const currentPosition = useSelector(
    locationSearchActorRef,
    (snapshot) => snapshot.context.currentPosition,
  );
  const baseLocation = useSelector(
    locationSearchActorRef,
    (snapshot) => snapshot.context.baseLocation,
  );

  const locationDistance = useMemo(() => {
    const isFeatureEnabled = isSurfaceDistanceForPredictionEnabled;
    const hasRequiredCoordinates = Boolean(latitude && longitude);

    if (!isFeatureEnabled || !hasRequiredCoordinates) return;

    const locationToCalculateDistanceFrom = isSurfaceDistanceForPredictionEnabled
      ? (currentPosition ?? baseLocation)
      : currentPosition;

    if (!locationToCalculateDistanceFrom) return;

    return getLocationDistance(locationToCalculateDistanceFrom, {
      latitude,
      longitude,
    });
  }, [isSurfaceDistanceForPredictionEnabled, latitude, longitude, currentPosition, baseLocation]);

  const shouldSurfaceDistance =
    isSurfaceDistanceForPredictionEnabled && typeof locationDistance?.formatted === 'string';

  const formattedAddress = useMemo(() => {
    if (!shouldSurfaceDistance) {
      return address;
    }

    const formattedDistance = formatMessage(messages.distance, {
      distance: locationDistance?.formatted,
    });

    return `${formattedDistance} · ${address}`;
  }, [address, formatMessage, locationDistance, shouldSurfaceDistance]);

  // ─── Flags ───────────────────────────────────────────────────────────

  const tooFarThresholdInMiles = useTooFarLocationThresholdInMiles({
    channel: locationOrderChannel,
    targetPosition: { latitude, longitude },
  });

  const locationDistanceInMiles = locationDistance?.raw ?? 0;
  const isTooFarLocationModalEnabled = tooFarThresholdInMiles > 0 && locationDistanceInMiles > tooFarThresholdInMiles; // prettier-ignore

  // ─────────────────────────────────────────────────────────────────────

  const isVisible = useIsFocused();
  const { match, minWidth } = useResponsive();

  // ─── State ───────────────────────────────────────────────────────────

  const [isDetailsModalVisible, setIsDetailsModalVisible] = useState(false);

  const cardRef = useRef<View>(null);

  // ─── Helpers ─────────────────────────────────────────────────────────

  const toggleDetailsModal = useCallback(() => {
    setIsDetailsModalVisible((currentValue) => !currentValue);
  }, []);

  const handleOnLayout = useCallback(() => {
    // @ts-expect-error - React ref type mismatch
    onLayout?.(cardRef, id);
  }, [id, onLayout]);

  const handleOnPress = useCallback(() => {
    const entryPoint = isDeliveryLocation ? 'delivery-cta' : 'card';

    onPress({ restaurantId: id, restaurantSlug, restaurantName: name }, { entryPoint });
  }, [isDeliveryLocation, onPress, id, restaurantSlug, name]);

  const handleOnFocus = useCallback(() => {
    onFocus?.(id);
  }, [id, onFocus]);

  const handleOnUnmount = useCallback(() => {
    onUmount?.(id);
  }, [id, onUmount]);

  // ─── Effects ─────────────────────────────────────────────────────────

  useEffect(() => handleOnUnmount, [handleOnUnmount]);

  // ─────────────────────────────────────────────────────────────────────

  const shouldDisplayDetailsModal = Boolean(withDetailsModal && isDetailsModalVisible && isVisible);
  const shouldDisableControls = isDisabled || isLoading;
  const shouldDisplayClosedNotice = locationOrderChannel !== 'delivery' && !acceptingOrders;
  const shouldDisplayETA = estimatedDeliveryTime !== undefined;
  const shouldDisplayAddressDetails = locationOrderChannel !== 'delivery';
  const shouldDisplayOrderCTA = minWidth.isSM && isDeliveryLocation && !withoutOrderNowBtn;

  // ─────────────────────────────────────────────────────────────────────

  const ctaLabel = isClosed
    ? formatMessage(messages.locationResultsCardOrderLater)
    : formatMessage(messages.locationResultsCardOrderNow);

  const hasAddressDetails = Boolean(city && state && zipCode);
  const addressDetails = hasAddressDetails ? `${city}, ${state} ${zipCode}` : undefined;
  const locationAddressDetails = shouldDisplayAddressDetails ? addressDetails : undefined;

  const locationResultA11yName = isDeliveryLocation
    ? formatMessage(messages.locationResultDelivery)
    : name;

  // ─── Fee Disclaimer ──────────────────────────────────────────────────

  const { shouldShowDeliveryFeeDisclaimer, shouldShowOutpostFeeDisclaimer } =
    useLocationResultsFeeDisclosure({ location });

  // ─── Styles ──────────────────────────────────────────────────────────

  // @ts-expect-error - React ref type mismatch
  const { isHovered, isFocused } = usePressableState(cardRef);

  const cardStyle = [
    match([styles.cardV2XS, styles.cardV2SM]),
    isDisabled ? styles.cardDisabled : undefined,
    !shouldDisableControls && (isHovered || isFocused) ? styles.cardHover : undefined,
    isFocused ? webOnlyStyles({ outlineStyle: 'dotted' }) : undefined,
    webOnlyStyles({
      outlineColor: theme.colors.GRAY,
      outlineOffset: theme.spacing['1'],
      outlineWidth: 2,
      transition: `opacity ${theme.transitions.base}ms`,
    }),
  ];
  const a11yLabel = shouldSurfaceDistance
    ? formatMessage(messages.a11yLabelWithDistance, {
        restaurant: locationResultA11yName,
        distance: locationDistance?.formatted,
      }) // prettier-ignore
    : formatMessage(messages.a11yLabel, { restaurant: locationResultA11yName });

  // ─────────────────────────────────────────────────────────────────────

  return (
    <View>
      <TouchableOpacity
        ref={cardRef}
        style={cardStyle}
        accessibilityRole="button"
        accessibilityLabel={a11yLabel}
        disabled={shouldDisableControls}
        onLayout={handleOnLayout}
        onPress={isTooFarLocationModalEnabled ? toggleDistanceModal : handleOnPress}
        onFocus={onFocus ? handleOnFocus : undefined}
        accessibilityState={{ disabled: isDisabled, busy: isLoading }}
      >
        <LocationResultCardContentV2
          locationName={name}
          locationOrderChannel={locationOrderChannel}
          address={formattedAddress}
          addressDetails={locationAddressDetails}
          estimatedDeliveryTime={shouldDisplayETA ? estimatedDeliveryTime : undefined}
          isLoading={isLoading}
          withLocationOrderChannel={shouldShowLocationOrderChannel}
          shouldShowDeliveryFeeDisclaimer={shouldShowDeliveryFeeDisclaimer}
          shouldShowOutpostFeeDisclaimer={shouldShowOutpostFeeDisclaimer}
          shouldDisplayClosedNotice={shouldDisplayClosedNotice}
        />
      </TouchableOpacity>

      {withDetailsModal ? (
        <IconLink
          name="IconBadgeInfo"
          accessibilityLabel={formatMessage(messages.detailsLinkA11y, { name })} // prettier-ignore
          onPress={toggleDetailsModal}
          disabled={shouldDisableControls}
          hitSlop={theme.spacing['1']}
          style={styles.infoButton}
        />
      ) : null}

      {shouldDisplayOrderCTA ? (
        <View style={styles.ctaContainer}>
          <Button
            size="large"
            isLoading={isLoading}
            onPress={isTooFarLocationModalEnabled ? toggleDistanceModal : handleOnPress}
          >
            {ctaLabel}
          </Button>
        </View>
      ) : null}

      {/* ─── Fee Disclosure ─────────────────────────────────────────────── */}

      {/* ─── Details Modal ──────────────────────────────────────────────── */}

      <LocationResultCardDetailsModal
        visible={shouldDisplayDetailsModal}
        location={location}
        locationOrderChannel={locationOrderChannel}
        addressDetails={addressDetails}
        isLoading={isLoading}
        isDisabled={isDisabled}
        onRequestClose={toggleDetailsModal}
        onOrderNowBtnPress={onPress}
      />

      {/* ─── Distance Modal ─────────────────────────────────────────────── */}

      <LocationResultCardDistanceModal
        isVisible={isDistanceModalVisible}
        location={isDelivery ? name || address : name}
        onRequestClose={toggleDistanceModal}
        onContinue={handleOnPress}
      />
    </View>
  );
});

// ─── Variations ──────────────────────────────────────────────────────────────

export const LocationResultCardLoading = memo(() => {
  const { match } = useResponsive();

  const isLocationSelectionUpdateEnabled = useFeatureFlag('CELS-5577-order-location-selection-ui-update-enabled'); // prettier-ignore

  const cardV2Style = match([styles.cardV2XS, styles.cardV2SM]);

  return (
    <View style={cardV2Style}>
      <LoadingPlaceholder
        rows={1}
        columns={1}
        rowHeight={100}
        borderRadius={theme.spacing['2']}
        palette={isLocationSelectionUpdateEnabled ? 'glass' : 'cream'}
      />
    </View>
  );
});

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  a11yLabel: {
    defaultMessage: 'Location result for {restaurant}',
    description: 'Location results card | a11y Label',
  },
  a11yLabelWithDistance: {
    defaultMessage: 'Location result for {restaurant}, {distance} miles away',
    description: 'Location results card | a11y Label',
  },
  distance: {
    defaultMessage: '{distance} miles away',
    description: 'Location results card | Distance',
  },
  detailsLinkA11y: {
    defaultMessage: 'Open location details for {name}',
    description: 'Location Search | Result | Details Link | A11y',
  },
  locationResultsCardOrderLater: {
    defaultMessage: 'Order later',
    description: 'Location results card | Order later',
  },
  locationResultsCardOrderNow: {
    defaultMessage: 'Order now',
    description: 'Location results card | Order now',
  },
  locationResultDelivery: {
    defaultMessage: 'Delivery',
    description: 'Location Result Card | Delivery',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  cardV2XS: {
    paddingVertical: LOCATION_RESULTS_CARD_HEIGHT_PADDING_VERTICAL_XS,
  },
  cardV2SM: {
    paddingVertical: LOCATION_RESULTS_CARD_HEIGHT_PADDING_VERTICAL_SM,
  },
  cardHover: {
    opacity: 0.7,
  },
  cardDisabled: {
    opacity: 0.6,
  },
  ctaContainer: {
    paddingTop: theme.spacing['1'],
  },
  infoButton: {
    position: 'absolute',
    top: theme.spacing['3'],
    right: 0,
  },
});

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Returns location distance (in miles) from the current position.
 */
function getLocationDistance(
  currentPosition: PositionCoordinates,
  locationPosition: PositionCoordinates,
) {
  const distanceInMiles = getLocationDistanceInMiles(currentPosition, locationPosition);

  return { formatted: distanceInMiles.toFixed(2), raw: distanceInMiles };
}

// ─── Types ───────────────────────────────────────────────────────────────────

type LocationResultsCardProps = {
  locationSearchActorRef: LocationSearchService; // prettier-ignore
  location: Location;
  withDetailsModal?: boolean;
  withoutOrderNowBtn?: boolean;
  shouldShowLocationOrderChannel?: boolean;
  isDisabled?: boolean;
  isLoading?: boolean;
  onLayout?: (listItemRef: RefObject<View>, listItemId: string) => void;
  onPress: OnSelectedLocationCardPress;
  onFocus?: (id: string | undefined) => void;
  onUmount?: (id: Location['id']) => void;
};
